import { Suspense, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { getMultiMeasureFormById } from '../../../scripts/api/multiMeasureFormApi';
import { message } from '../../shared/Message';
import api from '../../../scripts/api';
import ProgramMeasureDataDto from '../../../DTOs/Measure/ProgramMeasureDataDto';
import EditMeasureData from './EditMeasureData';
import { Container } from 'react-bootstrap';
import { Spinner } from 'react-bootstrap';
import ClientMeasureDataDto from '../../../DTOs/Measure/ClientMeasureDataDto';
import { rolePermissionLevels } from '../../../constants';
import { addMultiMeasureFormClientMeasureDataAsync, getMultiMeasureExternalFormByExternalIdAsync, getMultiMeasureExternalFormClientMeasureAsync, validateRecaptcha } from '../../../scripts/api/externalFormApi';
import UnknownClientMeasureDataDto from '../../../DTOs/Measure/UnknownClientMeasureDataDto';
import useRecaptcha from '../../shared/hooks/useRecaptcha';
import RecaptchaTokenDto from '../../../DTOs/shared/RecaptchaTokenDto';

function MultiMeasureFormFillPage({ userPermissions = null, isExternalForm = false, isPreview = false, handleClosePreview, previewMultiMeasureFormDto = null }) {

    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    const { id: paramId } = useParams();
    const id = isPreview ? 1 : paramId;

    const [multiMeasureForm, setMultiMeasureForm] = useState();
    const [measure, setMeasure] = useState();
    const [measureList, setMeasureList] = useState([]);
    const [errorMessage, setErrorMessage] = useState();
    const [measureCount, setMeasureCount] = useState();
    const [totalMeasures, setTotalMeasures] = useState();
    const [programId, setProgramId] = useState();
    const [showSkipButton, setShowSkipButton] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedClient, setSelectedClient] = useState();
    const [prevButtonClicked, setPrevButtonClicked] = useState(false);
    const [externalFormSubmitted, setExternalFormSubmitted] = useState(false);
    const [formCancelClicked, setFormCancelClicked] = useState(false);

    const navigate = useNavigate();
    useRecaptcha(isExternalForm && !isPreview, siteKey);

    const initializeComponent = async (id) => {
        try {
            if (!id) {
                throw new Error(`Invalid Multi-Measure Form ID: ${id}`);
            }

            // If isPreview = true, we use preview data
            // Otherwise, fetch by external ID or internal ID
            const result = isPreview
                ? previewMultiMeasureFormDto
                : isExternalForm
                    ? await getMultiMeasureExternalFormByExternalIdAsync(id)
                    : await getMultiMeasureFormById(id);
            setMultiMeasureForm(result);
            setTotalMeasures(result?.measures?.length || 0);
            setMeasureCount(1);
        } catch (err) {
            message.error(`Error fetching multi-measure form with ID: ${id}.`);
            console.error("Fetch error:", err);
        }
    };


    const fetchMeasure = async (meausreId) => {
        try {
            if (!meausreId) {
                throw new Error(`Invalid Measure Id : ${meausreId}`)
            }

            const measureDto = await (isExternalForm ? getMultiMeasureExternalFormClientMeasureAsync(meausreId) : api.getMeasureById(meausreId));
            if (measureDto) {
                const measureData = {
                    id: 0,
                    title: measureDto.name,
                    measureId: measureDto.id,
                    programId: multiMeasureForm.programId,
                    assessmentUsed: measureDto.assessmentUsed,
                    fields: measureDto.fieldDefinitions.fields,
                    cutoffScoreValue: measureDto.cutoffScoreValue,
                    cutoffScoreField: measureDto.cutoffScoreField,
                    cutoffScoreComparison: measureDto.cutoffScoreComparison.value
                };
                setProgramId(measureData.programId);
                setMeasure(measureData);
                setMeasureList([...measureList, measureData]);
            } else {
                throw new Error(`Error fetching measure with Id : ${meausreId}`)
            }
        } catch (err) {
            message.error(`Error Fetching Measure. Error :${err}`);
        }
    }

    useEffect(() => {
        if (!measure && multiMeasureForm?.measures?.[0]?.id) {
            !multiMeasureForm?.isSkippable && setShowSkipButton(false);
            fetchMeasure(multiMeasureForm?.measures[0]?.id);
        }
    }, [multiMeasureForm]);

    useEffect(() => {
        initializeComponent(id);
    }, [id]);

    const handleNavigation = () => {
        if (isPreview) {
            handleClosePreview();
            return;
        }

        if (isExternalForm) {
            setExternalFormSubmitted(true);
            return;
        }

        const isOrgStaff = userPermissions?.rolePermission === rolePermissionLevels.ORG_STAFF;

        if (isOrgStaff) {
            navigate('/Client');
        } else if (programId) {
            navigate(`/Program/${programId}`);
        }
    };

    useEffect(() => {
        if (measureCount) {
            if (prevButtonClicked && measureCount >= 1 && measureList && measureCount <= measureList.length) {
                // previous measure was selcted
                setMeasure(measureList[measureCount - 1]);
                setPrevButtonClicked(false);
            } else if (measureCount <= totalMeasures) {
                // next/save was completed
                if (measureCount <= measureList.length) {
                    // save/next was clicked after a prev button click
                    setMeasure(measureList[measureCount - 1]);
                } else {
                    //fetch new measure
                    const nextMeasureId = multiMeasureForm?.measures?.[measureCount - 1]?.id;
                    if (nextMeasureId) {
                        fetchMeasure(nextMeasureId);
                    }
                }
            } else if (measureCount > totalMeasures) {
                // final submit was done
                handleNavigation();
            } else {
                // do nothing
            }
        }

    }, [measureCount])

    const handleCancelClick = () => {
        setFormCancelClicked(true);
        handleNavigation();
    };

    const handleSkipClick = () => {
        try {
            if (!selectedClient && !isExternalForm) {
                throw new Error("Select a client.")
            }
            setMeasureCount(measureCount + 1);
        } catch (err) {
            setErrorMessage(`Error skipping: ${err}`);
        }
    };

    const handlePrevClick = () => {
        setPrevButtonClicked(true);
        setMeasureCount(measureCount - 1);
    };

    const handleFormSaveAndSubmit = async (values) => {
        try {
            setIsLoading(true);
            let dto;
            if (isExternalForm) {
                const actionName = 'clientMeasureDataForm';
                const token = !isPreview && await window.grecaptcha.execute(siteKey, { action: 'clientMeasureDataForm' });
                const recaptchaResponse = !isPreview && await validateRecaptcha(new RecaptchaTokenDto(token, actionName));

                if (isPreview || recaptchaResponse.isValid) {

                    dto = new UnknownClientMeasureDataDto({
                        ...values,
                        id: 0,
                    })
                } else {
                    throw new Error('reCAPTCHA validation failed.');
                }
            } else {
                if (!selectedClient) {
                    throw new Error("Select a client.")
                }
                dto = new ClientMeasureDataDto({
                    ...values,
                    id: 0,
                    clientId: selectedClient.value
                });
            }

            dto.setMeasureData(values);
            const prevMeasure = measureList.length && measureList.find(item => item.id === measure.id);

            if (prevMeasure &&
                dto.scoreDate.toString() === prevMeasure?.scoreDate?.toString() &&
                dto?.measureData?.toString() === prevMeasure?.measureData?.toString() &&
                (dto.clientId === prevMeasure?.clientId ||
                    (dto?.firstName === prevMeasure?.firstName &&
                        dto?.lastName === prevMeasure?.lastName &&
                        dto?.dateOfBirth === prevMeasure?.dateOfBirth))) {
                setMeasureCount(measureCount + 1);
                return;
            }
            const response = !isPreview && await (isExternalForm ? addMultiMeasureFormClientMeasureDataAsync(id, dto) : api.addClientMeasureData(dto.stringify()));
            if (!isPreview && response.message) {
                setErrorMessage(response.message);
            } else {
                measureList[measureCount - 1] = {
                    ...measureList[measureCount - 1],
                    id: Math.floor(Math.random() * 1000),
                    measureData: dto.measureData,
                    scoreDate: dto.scoreDate,
                    ...(isExternalForm ?
                        {
                            firstName: dto.firstName,
                            lastName: dto.lastName,
                            dateOfBirth: dto.dateOfBirth
                        } :
                        { clientId: dto.clientId }),
                };
                setMeasureCount(measureCount + 1);
                setErrorMessage('');
            }
        } catch (err) {
            console.log(`Error submitting measure data: ${err}`);
            setErrorMessage(`Error submitting measure data: ${err}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearchSelect = (selected) => {
        setSelectedClient(selected);
        setErrorMessage();
    }

    return (
        <Container>
            <Suspense fallback={<Spinner animation="border" className="sureimpact__spinner_export" />}>
                {multiMeasureForm && <h3>{multiMeasureForm?.name}</h3>}
                {isExternalForm && externalFormSubmitted ? `Your form submission ${formCancelClicked ? "was cancelled." : "has been received."} You may close this window.` : measure &&
                    <EditMeasureData
                        assessmentUsed={measure?.assessmentUsed}
                        data={isExternalForm ? new UnknownClientMeasureDataDto({ ...measure, firstName: measureList?.[0]?.firstName, lastName: measureList?.[0]?.lastName, dateOfBirth: measureList?.[0]?.dateOfBirth }) : new ProgramMeasureDataDto({ ...measure })}
                        measureCount={measureCount}
                        totalMeasures={totalMeasures}
                        showSkipButton={showSkipButton}
                        isClientMeasure={true}
                        isLoading={isLoading}
                        isExternalForm={isExternalForm}
                        selectedClient={selectedClient}
                        handleCancelClick={handleCancelClick}
                        handleSkipClick={handleSkipClick}
                        handlePrevClick={handlePrevClick}
                        handleFormSaveAndSubmit={handleFormSaveAndSubmit}
                        handleSearchSelect={handleSearchSelect}
                        errorMessage={errorMessage} />
                }
            </Suspense>
        </Container>
    );
}

export default MultiMeasureFormFillPage;
